import React from 'react';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import {
  Button,
  HStack,
  IconErrorCaution,
  ScrollView,
  Table,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { EntityType } from 'adc-sirius-module-common';
import { DeviceAssignmentRecord } from './hooks/useDeviceAssignmentRecords';
import { DeviceEntityType } from '../../models';

type Props = {
  isOpen: boolean;
  unassignedDevices: DeviceAssignmentRecord[];
  assignedDevices: DeviceAssignmentRecord[];
  entityName: string;
  entityType: DeviceEntityType;
  onClose: () => void;
  onSave: () => void;
};

/**
 * A modal component for confirming device assignment changes. Display static device assignment records.
 * @param {boolean} isOpen - whether the modal is open
 * @param {DeviceAssignmentRecord[]} unassignedDevices - list of devices to be unassigned from the specified entity
 * @param {DeviceAssignmentRecord[]} assignedDevices - list of devices to be assigned to the specified entity
 * @param {string} entityName - the display name of the entity for which devices are being assigned
 * @param {DeviceEntityType} entityType - the type of entity for which devices are being assigned
 * @param {() => void} onClose - function to close the modal
 * @param {() => void} onSave - function to save the device assignments
 * @returns {React.JSX.Element}
 */
export const DeviceAssignmentConfirmationModal = ({
  isOpen,
  unassignedDevices,
  assignedDevices,
  entityName,
  entityType,
  onClose,
  onSave,
}: Props) => {
  const entityLabelKeyByType = {
    [EntityType.Study]: 'Study',
    [EntityType.StudySite]: 'Site',
    [EntityType.StudySubject]: 'Subject',
  } as const;

  return (
    <Dialog open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="90%">
          <Dialog.Title>
            <Text type="$h3.strong">
              {i18n.t('Modal_DeviceAssignmentConfirmation.title')}
            </Text>
          </Dialog.Title>

          <ScrollView flex={1} minHeight={0} marginVertical="$4">
            <VStack gap="$2">
              <VStack gap="$4" paddingVertical="$4">
                <Text>
                  {i18n.t('Modal_DeviceAssignmentConfirmation.subtitle', {
                    primaryActionLabel: i18n.t('Common.ok'),
                  })}
                </Text>

                {entityType === EntityType.StudySubject && (
                  <Text type="$bodyBase.strong">
                    {i18n.t(
                      'Modal_DeviceAssignmentConfirmation.subjectWarning',
                    )}
                  </Text>
                )}
              </VStack>

              <VStack gap="$2" paddingVertical="$2">
                <Table
                  aria-label={i18n.t(
                    'Modal_DeviceAssignmentConfirmation.description',
                  )}
                  minHeight="auto"
                >
                  <Table.Header>
                    <Table.Row testID="header">
                      <Table.HeaderCell key="appName">
                        {i18n.t(
                          'Modal_DeviceAssignmentConfirmation.deviceName',
                        )}
                      </Table.HeaderCell>
                      <Table.HeaderCell key="previous-site">
                        {i18n.t(
                          'Modal_DeviceAssignmentConfirmation.previousSite',
                        )}
                      </Table.HeaderCell>
                      <Table.HeaderCell key="previous-study">
                        {i18n.t(
                          'Modal_DeviceAssignmentConfirmation.previousStudy',
                        )}
                      </Table.HeaderCell>
                      <Table.HeaderCell key="new">
                        {i18n.t(
                          `Modal_DeviceAssignmentConfirmation.new${entityLabelKeyByType[entityType]}`,
                        )}
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {unassignedDevices.map((device) => (
                      <Table.Row key={device.key} testID={device.key}>
                        <Table.TextCell key="appName">
                          {device.appName}
                        </Table.TextCell>
                        <Table.TextCell key="siteName">
                          {device.siteName}
                        </Table.TextCell>
                        <Table.TextCell key="studyName">
                          {device.studyName}
                        </Table.TextCell>
                        <Table.TextCell key="entityName">
                          {i18n.t(
                            'Modal_DeviceAssignmentConfirmation.unassigned',
                          )}
                        </Table.TextCell>
                      </Table.Row>
                    ))}
                    {assignedDevices.map((device) => (
                      <Table.Row key={device.key} testID={device.key}>
                        <Table.TextCell
                          key="appName"
                          startIcon={
                            device.studyKey || device.siteKey ? (
                              <span
                                title={i18n.t(
                                  'Modal_DeviceAssignmentConfirmation.reassignmentWarning',
                                )}
                                data-testID={`${device.key}-reassignment-warning`}
                              >
                                <IconErrorCaution color="$support.warning.default" />
                              </span>
                            ) : undefined
                          }
                        >
                          {device.appName}
                        </Table.TextCell>
                        <Table.TextCell key="siteName">
                          {device.siteName}
                        </Table.TextCell>
                        <Table.TextCell key="studyName">
                          {device.studyName}
                        </Table.TextCell>
                        <Table.TextCell key="entityName">
                          {entityName}
                        </Table.TextCell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </VStack>
            </VStack>
          </ScrollView>
          <HStack alignSelf="flex-end" gap="$2" flexShrink={0}>
            <Button
              variant="secondary"
              onPress={onClose}
              label={i18n.t('Common.goBack')}
              testID="go-back"
            />
            <Button
              variant="primary"
              onPress={onSave}
              label={i18n.t('Common.ok')}
              testID="confirm"
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
